import { useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import Storage from '@aws-amplify/storage';

import { getAllImageAssets } from '@dyl/apiClients/backendApi';
import createDatabaseManager from '@dyl/utils/createDatabaseManager';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import useAsync from '@dyl/hooks/useAsync';

/**
 * @typedef ImageAsset
 * @property {string} image_id
 * @property {string} created_at
 * @property {string} updated_at
 */

const { Provider: ImagesManager, use: useRaw } = createDatabaseManager({
    /** @returns {() => Promise<ImageAsset[]>} */
    requestHook: function useImagesRequest() {
        const { enqueueSnackbar } = useSnackbar();

        return useCallback(async () => {
            while (true) {
                try {
                    return await getAllImageAssets({ version: '0' } );
                }
                catch (error) {
                    await new Promise(resolve => enqueueSnackbar('Failed to load images', {
                        variant: 'error',
                        action: createSnackbarAction({
                            iconComponent: RefreshIcon,
                            onClick: resolve,
                        }),
                    }));
                }
            }
        }, []);
    },
    defaultValue: [],
    contextName: 'ImagesManager',
});

export default ImagesManager;

export function useImages() {
    const {
        data: images,
        refresh: refreshImages,
        status: imagesStatus,
    } = useRaw();

    // On mount (only), load images if unavailable
    useEffect(() => {
        if (imagesStatus === 'not started' || imagesStatus === 'error') {
            refreshImages();
        }
    }, []);

    return { images, refreshImages, imagesStatus };
}

export function useImage(databaseId) {
    /** @type {string | null} */
    const src = useAsync(async () => {
        if (!databaseId) return null;

        try {
            return await Storage.get(`images/${databaseId}`, {
                level: 'public',
                customPrefix: {
                    public: '',
                },
            });
        }
        catch (error) {
            console.error(error);
            throw error;
        }
    }, [databaseId]);

    return { src };
}